/**
 * Checklist - Figma `6377:88854`.
 *
 * A heading over a three-up grid of ticked items, with a hairline between each row of
 * three. Item text is `Desktop/Text/body-medium`; the tick is the `verified` badge at
 * 24px in `Color/Green/Green-600`.
 *
 * The rows are chunked in JavaScript rather than drawn with a CSS border trick, because
 * the separator sits between rows and not under the last one - which no `border-b` rule
 * expresses cleanly once the item count stops being a multiple of three.
 */
import { SectionShell } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import type { ChecklistSection } from '../../types/sections';

const PER_ROW = 3;

function chunk<T>(items: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

export default function Checklist({ data }: { data: ChecklistSection }) {
  const items = data.items ?? [];

  return (
    <SectionShell settings={data.settings} name="checklist">
      {data.heading ? <h2 className="max-w-[968px] text-h3 text-ink-strong">{data.heading}</h2> : null}

      {items.length ? (
        <div className="flex flex-col gap-10 px-5">
          {chunk(items, PER_ROW).map((row, rowIndex) => (
            <div key={rowIndex} className="contents">
              {rowIndex > 0 ? <hr className="border-0 border-t border-grey-300" /> : null}
              <div className="grid gap-8 md:grid-cols-3 md:gap-16">
                {row.map((item, i) => (
                  <div key={i} className="flex items-start gap-8">
                    <Icon name="verified" size={24} className="mt-0.5 text-green-600" />
                    <p className="text-body-md text-ink-strong">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </SectionShell>
  );
}
